import { useState } from 'react';
import { ActionIcon, Badge, Group, Indicator, Popover, Stack, Text } from '@mantine/core';
import { Link } from '@tanstack/react-router';
import { useMaterialControllerAppReceipts } from '@werkbank/shared';
import { Icon } from './navIcons';
import { formatDateTimeDE } from '../lib/format';
import { readLocal, writeLocal } from '../lib/storage';

const GESEHEN_KEY = 'wb.wareneingang.gesehenBis';

/**
 * Tobias 2026-09-25: Wareneingänge, die ein Monteur in der App erfasst hat,
 * sollen im Büro sichtbar werden — eine Glocke oben rechts mit der Zahl der
 * noch nicht gesehenen Eingänge der letzten 14 Tage. „Gesehen" merkt sich der
 * Browser (Zeitpunkt des jüngsten angezeigten Eingangs beim Öffnen).
 */
export function WareneingangGlocke({ enabled }: { enabled: boolean }) {
  const [offen, setOffen] = useState(false);
  const [gesehenBis, setGesehenBis] = useState<string>(() => readLocal(GESEHEN_KEY) ?? '');
  const eingaenge = useMaterialControllerAppReceipts({
    query: { enabled, refetchInterval: 60_000, retry: false },
  });
  if (!enabled) return null;
  const items = eingaenge.data?.items ?? [];
  const neu = items.filter((i) => i.deliveredAt > gesehenBis).length;

  // Öffnen merkt den jüngsten Eingang als gesehen; die „neu"-Marken bleiben
  // sichtbar, bis das Fenster wieder zugeht.
  const oeffnen = (ja: boolean) => {
    setOffen(ja);
    const juengster = items[0]?.deliveredAt;
    if (ja && juengster) writeLocal(GESEHEN_KEY, juengster);
    if (!ja) setGesehenBis(readLocal(GESEHEN_KEY) ?? '');
  };

  return (
    <Popover opened={offen} onChange={oeffnen} position="bottom-end" width={380} shadow="md">
      <Popover.Target>
        <Indicator
          label={neu}
          size={16}
          color="red"
          disabled={neu === 0}
          offset={4}
          data-testid="wareneingang-zahl"
        >
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={() => oeffnen(!offen)}
            title="Wareneingänge aus der Monteur-App"
            aria-label="Wareneingänge aus der Monteur-App"
            data-testid="wareneingang-glocke"
          >
            <Icon name="bell" />
          </ActionIcon>
        </Indicator>
      </Popover.Target>
      <Popover.Dropdown data-testid="wareneingang-liste">
        <Stack gap="xs">
          <Text fw={600} size="sm">
            Wareneingänge aus der App (14 Tage)
          </Text>
          {items.length === 0 && (
            <Text size="sm" c="dimmed">
              Keine Wareneingänge erfasst.
            </Text>
          )}
          <Stack gap={6} mah={360} style={{ overflowY: 'auto' }}>
            {items.map((i) => (
              <Stack
                key={i.id}
                gap={0}
                data-testid="wareneingang-eintrag"
                style={{ borderBottom: '1px solid var(--wb-border)', paddingBottom: 6 }}
              >
                <Group gap={6} wrap="nowrap" justify="space-between">
                  <Link
                    to="/orders/$orderId"
                    params={{ orderId: i.orderId }}
                    className="wb-tnum"
                    style={{ fontWeight: 600, fontSize: 'var(--mantine-font-size-sm)' }}
                    onClick={() => oeffnen(false)}
                  >
                    {i.orderNumber} · {i.orderTitle}
                  </Link>
                  {i.deliveredAt > gesehenBis && (
                    <Badge size="xs" color="red" variant="light">
                      neu
                    </Badge>
                  )}
                </Group>
                <Text size="sm" lineClamp={2}>
                  {i.text}
                </Text>
                <Text size="xs" c="dimmed">
                  {formatDateTimeDE(i.deliveredAt)}
                  {i.deliveredByName ? ` · ${i.deliveredByName}` : ''}
                  {i.attachmentCount > 0 ? ` · ${i.attachmentCount} Beleg(e)` : ''}
                  {i.customerName ? ` · ${i.customerName}` : ''}
                </Text>
              </Stack>
            ))}
          </Stack>
          <Link
            to="/material"
            style={{ fontSize: 'var(--mantine-font-size-sm)' }}
            onClick={() => oeffnen(false)}
          >
            Zu den Bestellungen
          </Link>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
